import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClientContext } from '@tanstack/react-query';
import { toFailure, type components } from '@cedarreachai/api-client';
import { DeferredLiveRegion } from '@/components/DeferredLiveRegion';
import { Button } from '@/components/ui/button';
import { refusalIdOf, request, setAuthFailureHandler } from '@/lib/api/http';
import { env } from '@/lib/env';
import { sessionKeys, workspaceScope } from '@/lib/queryKeys';
import { routeRequirement } from './capabilities';
import { SIGNED_OUT_PATH, currentPath, loginUrl } from './session';
import {
  bindStepUpMutation,
  deferStepUps,
  describeStepUpAction,
  discardDeferredStepUp,
  enqueueStepUp,
  markStepUpRethrown,
  stepUpBlock,
  stepUpReauthenticator,
  stepUpRequestOf,
  takeDeferredStepUp,
  stepUpScope,
  workspaceIdFromPath,
  type AnyMutation,
  type StepUpEntry,
  type StepUpReauthenticator,
} from './stepUp';
import { idempotencyKeyLifetimeMs, loadStepUps, saveStepUps } from './stepUpStore';

type Session = components['schemas']['Session'];

/** How deep {@link stepUpRefusalIdOf} follows an error's `cause` chain. */
const MAX_CAUSE_DEPTH = 8;

/**
 * The refusal id an error carries: its own ({@link refusalIdOf}), or that of
 * an error in its `cause` chain, so a `mutationFn` that wraps the refused
 * error (`new Error('…', { cause })`) is still bound to its refusal. An error
 * thrown without the refused one as its cause carries none.
 */
// Kept here, not in `stepUp.ts`: that module is imported by the mock
// handlers Playwright loads in Node, where `@/lib/api/http` (Vite env)
// cannot be evaluated.
export function stepUpRefusalIdOf(error: unknown): string | null {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    const id = refusalIdOf(current);
    if (id !== null) return id;
    if (typeof current !== 'object' || current === null || !('cause' in current)) return null;
    current = (current as { cause?: unknown }).cause;
  }
  return null;
}

/**
 * Turns the API's authentication answers into navigation (design doc §20.2).
 *
 * - `401 UNAUTHENTICATED` from any request (including `GET /me`): the session
 *   is missing or over, so the browser goes to `/auth/login` and returns here.
 * - `401 REAUTHENTICATION_REQUIRED`: the action needs a recent sign-in. A
 *   dialog restates the signed-in account, the workspace and the action that
 *   was refused — named from the request's route (`STEP_UP_ACTIONS`), §22.3:
 *   never a generic prompt — says nothing was changed, and offers to sign
 *   in again.
 *
 * Signing in again leaves the page (`/auth/login?reauth=1`) unless an
 * in-place re-authentication is registered — only the mock build registers
 * one (`src/mocks/browser.ts`), since MSW cannot answer a navigation. Then the
 * refused action is sent once more after the fresh sign-in (ADR-043) — the
 * one mutation whose request was refused, matched by the refusal's id
 * (ADR-049). Refusals queue behind one prompt that restates every refused
 * action (ADR-056): one fresh sign-in re-runs each bound mutation once, in the
 * order they were refused. "Not now" (or Escape) sends nothing and drops
 * nothing (ADR-062): the refused actions move to a labelled list that stays on
 * the page ("Waiting for a fresh sign-in"), each with "Retry sign-in" (the
 * prompt again, for that action) and "Discard"; a polite live region says
 * where they went. A refusal no mutation claimed is marked "not retried"
 * before the sign-in and listed again after it — never promised a retry — and
 * the prompt says which kind it is: a request outside any mutation, or one
 * made while a mutation ran that then failed with a different error (a
 * `mutationFn` that rethrew without `cause`, told from the mutation cache's
 * own `pending` and `error` events).
 *
 * The list survives a reload (ADR-068): every waiting action is kept in
 * `sessionStorage` for the signed-in user (`stepUpStore.ts`) and listed again
 * when the page comes back, with a polite announcement. A restored action
 * has no mutation to re-run; "Retry sign-in" re-runs the step-up flow and
 * then sends its kept request once, with the same `Idempotency-Key` (and
 * `If-Match`), so one that did commit is answered from the server's replay
 * store, not run twice. A rethrown one, one never claimed by a mutation, one
 * whose answer only its page can use, and any action older than the key's
 * lifetime (the session's `idempotency_key_retention_hours`, ADR-074) keep only "Discard", with the
 * reason beside the disabled "Retry sign-in". When the sign-in is a
 * navigation (a real deployment), the actions it answers are marked before
 * leaving and sent once when the page comes back.
 *
 * Navigation on `401 UNAUTHENTICATED` is not active under the local header
 * identity (`VITE_DEV_USER_ID`) or MSW, where there is no sign-in to go to.
 */
export interface AuthGateProps {
  readonly children: ReactNode;
  /**
   * Off under MSW, the local header identity and the unit-test runner, where
   * there is no sign-in to go to.
   */
  readonly enabled?: boolean;
  /** How to leave the app for the sign-in page (injectable for tests). */
  readonly navigate?: (url: string) => void;
  /** In-place re-authentication (the mock build's; injectable for tests). */
  readonly reauthenticate?: StepUpReauthenticator | null;
  /** The clock expiry is judged by (injectable for tests). */
  readonly now?: () => number;
}

const assignLocation = (url: string): void => window.location.assign(url);
const systemNow = (): number => Date.now();

const defaultEnabled = !(env.mswEnabled || env.devUserId || import.meta.env.MODE === 'test');

type Phase = 'asking' | 'reauthenticating' | 'failed';

/** The deferred list's heading, where focus lands after a discard. */
const DEFERRED_TITLE_ID = 'step-up-deferred-title';

export function AuthGate({
  children,
  enabled = defaultEnabled,
  navigate = assignLocation,
  reauthenticate,
  now = systemNow,
}: AuthGateProps) {
  const { t } = useTranslation();
  const client = useContext(QueryClientContext);
  const inPlace = reauthenticate === undefined ? stepUpReauthenticator() : reauthenticate;
  const [queue, setQueue] = useState<readonly StepUpEntry[]>([]);
  // "Not now": refusals kept for later, never sent without a fresh sign-in.
  const [deferred, setDeferred] = useState<readonly StepUpEntry[]>([]);
  const deferredRef = useRef<readonly StepUpEntry[]>([]);
  // What the polite region last said (it exists before its words).
  const [announcement, setAnnouncement] = useState('');
  // Mutations between their `pending` and their outcome, and, per refusal,
  // which of them were in flight when it arrived.
  const inFlight = useRef(new Set<AnyMutation>());
  const inFlightAtRefusal = useRef(new Map<string, ReadonlySet<AnyMutation>>());
  // After a sign-in: what was sent again and what was not retried.
  const [report, setReport] = useState<{
    readonly sent: number;
    readonly notRetried: readonly StepUpEntry[];
  } | null>(null);
  const [phase, setPhase] = useState<Phase>('asking');
  const [redirecting, setRedirecting] = useState(false);
  const returnFocus = useRef<Element | null>(null);
  // The latest queue, for the handlers registered once below.
  const queueRef = useRef<readonly StepUpEntry[]>([]);
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Who is signed in (`GET /me`, as cached): the stored list is theirs alone.
  const [user, setUser] = useState<string | null>(null);
  // How long the server keeps a key (`/me`, ADR-074): the "expired" cut-off.
  const [lifetimeMs, setLifetimeMs] = useState(() => idempotencyKeyLifetimeMs(null));
  useEffect(() => {
    if (!client) return;
    const read = () => {
      const session = client.getQueryData<Session>(sessionKeys.me());
      setUser(session?.user.id ?? null);
      setLifetimeMs(idempotencyKeyLifetimeMs(session));
    };
    read();
    return client.getQueryCache().subscribe((event) => {
      const [scope, leaf] = event.query.queryKey as readonly unknown[];
      if (scope === 'session' && leaf === 'me') read();
    });
  }, [client]);
  const lifetimeHours = Math.round(lifetimeMs / (60 * 60 * 1000));
  // Whose stored list was read into this page; nothing is written before.
  const restoredFor = useRef<string | null>(null);
  // Set while the page leaves for a sign-in: what is stored stays as written.
  const leaving = useRef(false);
  // What a restored action's request answered, shown until dismissed.
  const [outcome, setOutcome] = useState<string | null>(null);

  /** Every waiting action, deferred first, none twice: what is stored. */
  const waiting = () => deferStepUps(deferredRef.current, queueRef.current);
  const persist = (resume?: ReadonlySet<string>) => {
    if (!user || restoredFor.current !== user || leaving.current) return;
    saveStepUps(user, waiting(), now(), resume);
  };

  useEffect(() => {
    if (!enabled && !inPlace) return;
    setAuthFailureHandler((failure, context) => {
      if (failure === 'reauthenticate') {
        const workspaceId =
          routeRequirement(context.method, context.path)?.workspaceId ??
          workspaceIdFromPath(window.location.pathname);
        if (queueRef.current.length === 0) {
          // Unless the report of an earlier sign-in is open: focus returns to
          // where it was before that one.
          if (!document.getElementById('step-up-title'))
            returnFocus.current = document.activeElement;
          setPhase('asking');
        }
        const entry: StepUpEntry = {
          id: context.refusalId,
          ...stepUpScope(client, workspaceId),
          workspaceId,
          brandId: routeRequirement(context.method, context.path)?.params['brand_id'] ?? null,
          action: describeStepUpAction(context, client),
          mutation: null,
          createdAt: now(),
          request: stepUpRequestOf(context),
        };
        inFlightAtRefusal.current.set(context.refusalId, new Set(inFlight.current));
        queueRef.current = enqueueStepUp(queueRef.current, entry);
        setQueue((q) => enqueueStepUp(q, entry));
        return;
      }
      if (!enabled) return;
      if (window.location.pathname === SIGNED_OUT_PATH) return;
      setRedirecting(true);
      navigate(loginUrl(currentPath()));
    });
    return () => setAuthFailureHandler(null);
  }, [enabled, inPlace, navigate, client, now]);

  // The mutation a refusal belongs to is the one whose error carries the
  // refusal's id (directly or as its `cause`): told by the cache when that
  // mutation fails, so two step-up mutations in flight at once are never
  // confused. A mutation that fails with an error carrying no refusal marks
  // the refusals that arrived while it was in flight as "rethrown" — a
  // reading for the prompt, never a binding (ADR-062).
  useEffect(() => {
    if (!client) return;
    return client.getMutationCache().subscribe((event) => {
      if (event.type === 'removed') {
        inFlight.current.delete(event.mutation as unknown as AnyMutation);
        return;
      }
      if (event.type !== 'updated') return;
      const mutation = event.mutation as unknown as AnyMutation;
      const kind = event.action.type;
      if (kind === 'pending') {
        inFlight.current.add(mutation);
        return;
      }
      if (kind === 'success') {
        inFlight.current.delete(mutation);
        return;
      }
      if (kind !== 'error') return;
      inFlight.current.delete(mutation);
      const refusalId = stepUpRefusalIdOf(event.action.error);
      if (refusalId !== null) {
        queueRef.current = bindStepUpMutation(queueRef.current, refusalId, mutation);
        setQueue((q) => bindStepUpMutation(q, refusalId, mutation));
        deferredRef.current = bindStepUpMutation(deferredRef.current, refusalId, mutation);
        setDeferred((d) => bindStepUpMutation(d, refusalId, mutation));
        return;
      }
      const during = new Set<string>();
      for (const [id, running] of inFlightAtRefusal.current) {
        if (running.has(mutation)) during.add(id);
      }
      if (during.size === 0) return;
      queueRef.current = markStepUpRethrown(queueRef.current, during);
      setQueue((q) => markStepUpRethrown(q, during));
      deferredRef.current = markStepUpRethrown(deferredRef.current, during);
      setDeferred((d) => markStepUpRethrown(d, during));
    });
  }, [client]);

  const actionText = (entry: StepUpEntry) =>
    t(entry.action.key, {
      ...entry.action.values,
      ...(entry.action.role ? { role: t(`settings.members.roles.${entry.action.role}`) } : {}),
    });

  /** Whether `entry` is sent again after the next sign-in (re-run or re-sent). */
  const resendable = (entry: StepUpEntry, at: number = now()) =>
    stepUpBlock(entry, at, lifetimeMs) === null &&
    (entry.mutation !== null || (entry.restored?.bound === true && entry.request != null));

  /**
   * Sends a restored action's kept request once, with its own key and
   * `If-Match`: a request that did commit is replayed by the server, not
   * run again. Its outcome is announced and shown; a fresh refusal queues
   * itself through the handler above.
   */
  const resend = async (entry: StepUpEntry) => {
    const kept = entry.request;
    if (!kept) return;
    const action = actionText(entry);
    let said: string;
    try {
      await request(kept.path, {
        method: kept.method,
        ...(kept.body ? { body: kept.body } : {}),
        ...(kept.idempotencyKey ? { idempotencyKey: kept.idempotencyKey } : {}),
        ...(kept.ifMatchVersion ? { ifMatchVersion: kept.ifMatchVersion } : {}),
      });
      said = t('auth.stepUp.restoredSent', { action });
      if (client) {
        if (entry.workspaceId)
          void client.invalidateQueries({ queryKey: workspaceScope(entry.workspaceId) });
        void client.invalidateQueries({ queryKey: sessionKeys.me() });
      }
    } catch (error) {
      const failure = toFailure(error);
      const code = failure && 'code' in failure ? String(failure.code) : (failure?.kind ?? '');
      said = t('auth.stepUp.restoredFailed', { action, code });
    }
    setOutcome(said);
    setAnnouncement(said);
  };

  // Once per signed-in user: the list stored before a reload comes back.
  // Actions marked before leaving for a sign-in are sent once, now — the
  // mark is consumed by saving the list without them first.
  useEffect(() => {
    if (!user || restoredFor.current === user) return;
    restoredFor.current = user;
    const { entries, resume } = loadStepUps(user, now());
    const toSend = entries.filter((e) => resume.has(e.id) && resendable(e));
    const kept = entries.filter((e) => !toSend.includes(e));
    if (kept.length > 0) {
      const next = deferStepUps(deferredRef.current, kept);
      deferredRef.current = next;
      setDeferred(next);
      setAnnouncement(t('auth.stepUp.restoredAnnounce', { total: next.length }));
    }
    saveStepUps(user, waiting(), now());
    void (async () => {
      for (const entry of toSend) await resend(entry);
    })();
    // Read once per user; the helpers are this render's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Every change to what is waiting is written through.
  useEffect(() => {
    persist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, deferred, user]);

  const head = queue[0] ?? null;
  const open = head !== null || report !== null;
  const view: 'ask' | 'report' | null = head ? 'ask' : report ? 'report' : null;
  useEffect(() => {
    // On opening, on switching to the report, and back on the button after
    // a failed attempt (a disabled button loses focus while the sign-in runs).
    if (view !== null && phase !== 'reauthenticating')
      document.getElementById('step-up-confirm')?.focus();
  }, [view, phase]);

  const restoreFocus = () => {
    if (returnFocus.current instanceof HTMLElement) returnFocus.current.focus();
  };

  /**
   * "Not now": nothing is sent and nothing is dropped. Every refused action
   * moves to the deferred list, which says so in the polite region.
   */
  const deferAll = () => {
    const next = deferStepUps(deferredRef.current, queueRef.current);
    deferredRef.current = next;
    setDeferred(next);
    queueRef.current = [];
    setQueue([]);
    setPhase('asking');
    setAnnouncement(t('auth.stepUp.deferredAnnounce', { total: next.length }));
    if (report === null) restoreFocus();
  };

  /** "Retry sign-in" on one deferred action: the prompt again, for it. */
  const retryDeferred = (refusalId: string) => {
    const { entry, rest } = takeDeferredStepUp(deferredRef.current, refusalId);
    // A blocked action keeps only "Discard" (its button says why).
    if (!entry || stepUpBlock(entry, now(), lifetimeMs) !== null) return;
    deferredRef.current = rest;
    setDeferred(rest);
    if (queueRef.current.length === 0) {
      // Back to the list (or, when it is now empty, where focus was before
      // the first prompt) once this prompt closes.
      if (rest.length > 0) returnFocus.current = document.getElementById(DEFERRED_TITLE_ID);
      setPhase('asking');
    }
    queueRef.current = enqueueStepUp(queueRef.current, entry);
    setQueue((q) => enqueueStepUp(q, entry));
  };

  /** "Discard" on one deferred action: forgotten, never sent. */
  const discardDeferred = (entry: StepUpEntry) => {
    const rest = discardDeferredStepUp(deferredRef.current, entry.id);
    deferredRef.current = rest;
    setDeferred(rest);
    setAnnouncement(
      t('auth.stepUp.discardedAnnounce', { action: actionText(entry), total: rest.length }),
    );
    // Focus the list's heading while anything is left in it, else go back.
    if (rest.length > 0) {
      requestAnimationFrame(() => document.getElementById(DEFERRED_TITLE_ID)?.focus());
    } else restoreFocus();
  };
  const closeReport = () => {
    setReport(null);
    if (queueRef.current.length === 0) restoreFocus();
  };

  const confirm = async () => {
    if (!head) return;
    if (!inPlace) {
      // The sign-in is a navigation: mark what it answers, to be sent once
      // when the page comes back (ADR-068); the rest keeps waiting.
      const at = now();
      const resume = new Set(
        queueRef.current.filter((e) => resendable(e, at) && e.request != null).map((e) => e.id),
      );
      persist(resume);
      leaving.current = true;
      navigate(loginUrl(currentPath(), true));
      return;
    }
    setPhase('reauthenticating');
    try {
      await inPlace(currentPath());
    } catch {
      setPhase('failed');
      return;
    }
    // Read now, not from the render that opened the prompt: a mutation is
    // bound when its error reaches the cache. Every refusal queued so far is
    // answered by this one sign-in.
    const answered = queueRef.current;
    queueRef.current = [];
    setQueue([]);
    setPhase('asking');
    const at = now();
    const bound = answered.filter((e) => resendable(e, at));
    const notRetried = answered.filter((e) => !resendable(e, at));
    if (notRetried.length > 0) setReport({ sent: bound.length, notRetried });
    else restoreFocus();
    // Each refused action once more on the fresh session, in the order it
    // was refused — and nothing else. Its outcome is the mutation's own,
    // shown where it was taken. (A mutation called without arguments re-runs
    // without them.)
    for (const entry of bound) {
      const mutation = entry.mutation;
      if (mutation) await mutation.execute(mutation.state.variables).catch(() => undefined);
      // Restored after a reload: its kept request, once, with its own key.
      else await resend(entry);
    }
  };

  const shown = view === 'ask' ? queue : (report?.notRetried ?? []);
  const scope = head ?? report?.notRetried[0] ?? null;

  return (
    <>
      {redirecting ? (
        <p role="status" aria-live="polite" className="sr-only">
          {t('auth.redirecting')}
        </p>
      ) : null}
      {children}
      {/* Mounted with the first deferral; committed empty, filled a frame
          later (ADR-056), so the deferral is announced. Never mounted
          otherwise: a page's own status regions stay the only ones. */}
      {announcement ? (
        <DeferredLiveRegion className="sr-only" data-testid="step-up-deferred-status">
          {announcement}
        </DeferredLiveRegion>
      ) : null}
      {outcome ? (
        <div
          data-testid="step-up-outcome"
          className="fixed bottom-4 right-4 z-40 flex w-[min(24rem,calc(100vw-2rem))] items-start gap-2 rounded-md border border-border bg-background p-3 text-sm shadow-lg"
        >
          <p className="flex-1">{outcome}</p>
          <Button size="sm" variant="ghost" onClick={() => setOutcome(null)}>
            {t('auth.stepUp.outcomeDismiss')}
          </Button>
        </div>
      ) : null}
      {deferred.length > 0 ? (
        <section
          aria-labelledby={DEFERRED_TITLE_ID}
          data-testid="step-up-deferred"
          className="fixed bottom-4 left-4 z-40 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 rounded-md border border-border bg-background p-3 text-sm shadow-lg"
        >
          <h2 id={DEFERRED_TITLE_ID} tabIndex={-1} className="font-semibold">
            {t('auth.stepUp.deferredTitle', { total: deferred.length })}
          </h2>
          <p className="text-muted-foreground">{t('auth.stepUp.deferredBody')}</p>
          <ul className="flex flex-col gap-2">
            {deferred.map((entry) => {
              const action = actionText(entry);
              const block = stepUpBlock(entry, now(), lifetimeMs);
              const reasonId = `step-up-deferred-reason-${entry.id}`;
              return (
                <li
                  key={entry.id}
                  data-refusal-id={entry.id}
                  data-restored={entry.restored ? 'yes' : undefined}
                  data-blocked={block ?? undefined}
                  className="flex flex-col gap-1"
                >
                  <span data-testid="step-up-deferred-action">{action}</span>
                  <span className="text-muted-foreground">
                    {t('auth.stepUp.deferredScope', {
                      account: entry.account ?? t('auth.stepUp.accountUnknown'),
                      workspace: entry.workspace ?? t('auth.stepUp.workspaceUnknown'),
                    })}
                  </span>
                  {block ? (
                    <span
                      id={reasonId}
                      data-testid="step-up-deferred-reason"
                      className="text-muted-foreground"
                    >
                      {t(`auth.stepUp.blocked.${block}`, { hours: lifetimeHours })}
                    </span>
                  ) : null}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t('auth.stepUp.deferredRetryLabel', { action })}
                      // Focusable, so the reason is read with it; it does nothing.
                      aria-disabled={block ? true : undefined}
                      aria-describedby={block ? reasonId : undefined}
                      onClick={() => {
                        if (!block) retryDeferred(entry.id);
                      }}
                      disabled={queue.length > 0}
                    >
                      {t('auth.stepUp.deferredRetry')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('auth.stepUp.deferredDiscardLabel', { action })}
                      onClick={() => discardDeferred(entry)}
                      disabled={queue.length > 0}
                    >
                      {t('auth.stepUp.deferredDiscard')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {open && scope ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="step-up-title"
            aria-describedby="step-up-body"
            data-testid="step-up-dialog"
            data-view={view ?? undefined}
            data-refusal-id={scope.id}
            className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg"
            onKeyDown={(event) => {
              if (event.key === 'Escape' && phase !== 'reauthenticating') {
                if (view === 'ask') deferAll();
                else closeReport();
              }
              // Keep focus inside the dialog: it is modal.
              if (event.key === 'Tab') {
                const buttons = Array.from(
                  event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
                );
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <h2 id="step-up-title" className="text-base font-semibold">
              {view === 'ask' ? t('auth.stepUpTitle') : t('auth.stepUp.doneTitle')}
            </h2>
            <div id="step-up-body" className="mt-2 flex flex-col gap-2 text-sm">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="font-medium">{t('auth.stepUp.accountLabel')}</dt>
                <dd data-testid="step-up-account">
                  {scope.account ?? t('auth.stepUp.accountUnknown')}
                </dd>
                <dt className="font-medium">{t('auth.stepUp.workspaceLabel')}</dt>
                <dd data-testid="step-up-workspace">
                  {scope.workspace ?? t('auth.stepUp.workspaceUnknown')}
                </dd>
              </dl>
              {view === 'report' && report ? (
                <p role="status" aria-live="polite" data-testid="step-up-report">
                  {t('auth.stepUp.doneBody', {
                    sent: report.sent,
                    notRetried: report.notRetried.length,
                  })}
                </p>
              ) : null}
              <p className="font-medium" id="step-up-actions-label">
                {t('auth.stepUp.actionsLabel', { total: shown.length })}
              </p>
              <ul
                aria-labelledby="step-up-actions-label"
                data-testid="step-up-actions"
                className="flex flex-col gap-1"
              >
                {shown.map((entry) => {
                  const retried = view === 'ask' && inPlace !== null && resendable(entry);
                  const block = stepUpBlock(entry, now(), lifetimeMs);
                  return (
                    <li
                      key={entry.id}
                      data-refusal-id={entry.id}
                      data-retry={retried ? 'yes' : 'no'}
                      className="flex flex-col"
                    >
                      <span data-testid="step-up-action">{actionText(entry)}</span>
                      {inPlace ? (
                        <span
                          data-testid="step-up-retry"
                          data-reason={
                            retried
                              ? undefined
                              : (block ?? (entry.rethrown ? 'rethrown' : 'outside_mutation'))
                          }
                          className="text-muted-foreground"
                        >
                          {retried
                            ? t('auth.stepUp.willRetry')
                            : block === 'expired' ||
                                block === 'one_time_answer' ||
                                block === 'not_kept'
                              ? t(`auth.stepUp.blocked.${block}`, { hours: lifetimeHours })
                              : entry.rethrown
                                ? t('auth.stepUp.notRetriedRethrown')
                                : t('auth.stepUp.notRetried')}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {view === 'ask' ? (
                <p className="text-muted-foreground">
                  {inPlace
                    ? t('auth.stepUp.effectQueue', { total: queue.length })
                    : t('auth.stepUpBody')}
                </p>
              ) : null}
              {phase === 'reauthenticating' ? (
                <p role="status" aria-live="polite">
                  {t('auth.stepUp.reauthenticating')}
                </p>
              ) : null}
              {phase === 'failed' ? (
                <p role="alert" className="font-medium">
                  {t('auth.stepUp.failed', { total: queue.length })}
                </p>
              ) : null}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {view === 'ask' ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={deferAll}
                    disabled={phase === 'reauthenticating'}
                  >
                    {t('auth.stepUpCancel')}
                  </Button>
                  <Button
                    id="step-up-confirm"
                    onClick={() => void confirm()}
                    disabled={phase === 'reauthenticating'}
                  >
                    {t('auth.stepUpConfirm')}
                  </Button>
                </>
              ) : (
                <Button id="step-up-confirm" onClick={closeReport}>
                  {t('auth.stepUp.close')}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
